import React, { useMemo, useState } from "react";
import { Table, Badge, Button, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  markERAClaimAllocated,
  markERAClaimDismissed,
} from "../../afinanceapi";
import CreateReceiptModal from "../Accounts/Receipt.jsx";
import {
  getAccountByPatientId,
  getPatientIdByMedAidDepCode,
  checkAccountByMedicalAid,
} from "../../api.js";
import {
  statusMeta,
  classOfStatus,
  formatGroupDate,
  fmtZAR,
} from "./eraStatus";

/**
 * ERA detail view, grouped for readability while preserving ERA integrity:
 *
 *   ERA (this page) → Patient → Matched / Unmatched → claim dates
 *
 * Every <era:Claim> in the ERA is flattened to its date-group outcomes and
 * regrouped by the person the scheme paid for (medical-aid number + dependant
 * code). Within each patient the matched claims are shown first, then the
 * unmatched ones with Allocate / Dismiss actions. Each date is a single
 * summary row; its individual lines + reason codes expand on demand so the
 * common case stays scannable.
 */

// Short, distinct tariff summary for a date-group's lines.
function tariffSummary(lines) {
  const codes = [...new Set((lines || []).map((l) => l.tariffCode).filter(Boolean))];
  if (codes.length === 0) return "—";
  if (codes.length <= 2) return codes.join(", ");
  return `${codes.slice(0, 2).join(", ")} +${codes.length - 2}`;
}

// Flatten eraClaims[] → one row per date-group, regrouped by patient.
function buildPatientGroups(eraClaims) {
  const byPatient = new Map();

  (eraClaims || []).forEach((ec, ecIdx) => {
    const hdr = ec?.eraClaim || {};
    const patientName =
      hdr.patientName ||
      `${hdr.patientFirstName || ""} ${hdr.patientLastName || ""}`.trim() ||
      "(unknown patient)";
    const key = `${hdr.accountMedicalAidNumber || "noma"}|${hdr.dependantCode || ""}|${patientName.toLowerCase()}`;

    if (!byPatient.has(key)) {
      byPatient.set(key, {
        key,
        patientName,
        accountMedicalAidNumber: hdr.accountMedicalAidNumber || "",
        dependantCode: hdr.dependantCode || "",
        accountReference: hdr.accountReference || "",
        matched: [],
        unmatched: [],
      });
    }
    const group = byPatient.get(key);
    const lineByKey = new Map((ec.lines || []).map((l) => [String(l.key), l]));

    (ec.dateGroups || []).forEach((g, gIdx) => {
      const lines = (g.lineKeys || [])
        .map((k) => lineByKey.get(String(k)))
        .filter(Boolean);
      const row = {
        id: `${ecIdx}-${gIdx}-${g.date || "nodate"}`,
        schemeClaimId: hdr.schemeClaimId,
        treatmentDate: hdr.treatmentDate,
        group: g,
        lines,
      };
      (classOfStatus(g.status) === "matched" ? group.matched : group.unmatched).push(row);
    });
  });

  const byDate = (a, b) =>
    String(a.group.date || "").localeCompare(String(b.group.date || ""));
  const groups = [...byPatient.values()];
  groups.forEach((g) => {
    g.matched.sort(byDate);
    g.unmatched.sort(byDate);
  });
  groups.sort((a, b) => a.patientName.localeCompare(b.patientName));
  return groups;
}

export default function EraPatientGroups({ eraClaims, eraLogId, onMutated }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // row id currently mutating
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [receiptModalShow, setReceiptModalShow] = useState(false);
  const [allocCtx, setAllocCtx] = useState({ patient: null, account: null });

  const groups = useMemo(() => buildPatientGroups(eraClaims), [eraClaims]);

  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleAllocate = async (patient, row) => {
    setError("");
    try {
      const patientId = await getPatientIdByMedAidDepCode(
        patient.accountMedicalAidNumber,
        patient.dependantCode,
      );
      if (!patientId) {
        alert("No patient found for the specified medical aid and dependant code.");
        return;
      }
      const account = await getAccountByPatientId(patientId);
      if (!account) {
        alert("No account found for the selected patient.");
        return;
      }
      setAllocCtx({
        patient: patientId,
        account,
        rowId: row.id,
        schemeClaimId: row.schemeClaimId,
        treatmentDate: row.treatmentDate,
        dateGroupDate: row.group.date,
      });
      setReceiptModalShow(true);
    } catch (err) {
      console.error("Allocate error:", err);
      setError(err?.message || "Failed to start allocation.");
    }
  };

  const handleReceiptClosed = async () => {
    // Receipt either posted or was cancelled — either way the user actioned it,
    // so flag the date-group allocated server-side and refresh from the parent.
    setReceiptModalShow(false);
    if (!allocCtx?.schemeClaimId) return;
    setBusy(allocCtx.rowId);
    try {
      await markERAClaimAllocated({
        eraLogId,
        schemeClaimId: allocCtx.schemeClaimId,
        treatmentDate: allocCtx.treatmentDate,
        dateGroupDate: allocCtx.dateGroupDate,
      });
      if (onMutated) await onMutated();
    } catch (err) {
      console.error("Mark-allocated error:", err);
      setError(err?.message || "Failed to mark as allocated.");
    } finally {
      setBusy(null);
      setAllocCtx({ patient: null, account: null });
    }
  };

  const handleDismiss = async (row) => {
    if (
      !window.confirm(
        `Dismiss the ${formatGroupDate(row.group.date)} claim? This removes it from the unmatched list.`,
      )
    )
      return;
    setError("");
    setBusy(row.id);
    try {
      await markERAClaimDismissed({
        eraLogId,
        schemeClaimId: row.schemeClaimId,
        treatmentDate: row.treatmentDate,
        dateGroupDate: row.group.date,
      });
      if (onMutated) await onMutated();
    } catch (err) {
      console.error("Dismiss error:", err);
      setError(err?.message || "Failed to dismiss.");
    } finally {
      setBusy(null);
    }
  };

  const handleGoToAccount = async (patient) => {
    try {
      const acct = await checkAccountByMedicalAid(patient.accountMedicalAidNumber);
      if (acct?._id) {
        navigate(`/accounts/${acct._id}`);
      } else {
        alert("No account found for the specified medical aid number.");
      }
    } catch (err) {
      console.error("Go-to-account error:", err);
      alert("An error occurred while navigating to the account.");
    }
  };

  // Badge for a date-group — starts from the shared status meta, then overlays
  // the two data-driven Matched sub-states STATUS_META can't express:
  //  - manual match: finance matched it by hand from the account worklist
  //    (backend stamps manuallyAllocated / statusAfter "Manually Allocated");
  //  - patient liable: the practice was paid NOTHING and the whole amount
  //    shifted to the patient (medical aid didn't pay).
  const badgeFor = (g) => {
    const base = statusMeta(g.status);
    const sub = g.subtotals || {};
    const manuallyMatched =
      g.manuallyAllocated === true ||
      g.matchedClaim?.statusAfter === "Manually Allocated";
    if (g.status === "allocated" && manuallyMatched) {
      return { label: "Matched - Manually", bg: "success", icon: "fa-user-check" };
    }
    if (
      (g.status === "allocated" || g.status === "skipped_finalised") &&
      Number(sub.paid) === 0 &&
      Number(sub.movedToPatient) > 0
    ) {
      return { label: "Matched - Patient Liable", bg: "success", icon: "fa-user" };
    }
    return base;
  };

  // Expandable line detail shared by desktop + mobile. Plain render helpers
  // (not inline components) so React doesn't remount the subtree each render.
  const renderLineDetail = (row) => (
    <div className="p-2" style={{ background: "var(--acc-row-hover-bg, rgba(207,226,255,0.18))" }}>
      <Table size="sm" borderless className="mb-0 small">
        <thead>
          <tr className="text-muted">
            <th>Line</th>
            <th>Tariff</th>
            <th className="acc-list-num">Claimed</th>
            <th className="acc-list-num">Paid</th>
            <th className="acc-list-num">To Patient</th>
            <th>Reason codes</th>
          </tr>
        </thead>
        <tbody>
          {row.lines.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-muted">No line detail recorded.</td>
            </tr>
          ) : (
            row.lines.map((line, i) => (
              <tr key={`${row.id}-line-${i}`}>
                <td>{line.eraLineNumber ?? "—"}</td>
                <td>{line.tariffCode || "—"}</td>
                <td className="acc-list-num">{fmtZAR(line.claimedAmount || 0)}</td>
                <td className="acc-list-num">{fmtZAR(line.paidAmount || 0)}</td>
                <td className="acc-list-num">{fmtZAR(line.patientPortion || 0)}</td>
                <td>
                  {Array.isArray(line.reasonCodes) && line.reasonCodes.length > 0 ? (
                    line.reasonCodes.map((r, k) => (
                      <div key={k}>
                        <Badge bg="warning" text="dark" className="me-2 fw-semibold">
                          {r.code}
                        </Badge>
                        {r.description}
                      </div>
                    ))
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );

  // One matched/unmatched sub-block for a patient.
  const renderClaimsBlock = ({ title, tone, rows, actionable, patient }) => {
    if (rows.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className={`small fw-bold text-${tone}`}>{title}</span>
          <Badge bg={tone === "success" ? "success" : "danger"} pill>
            {rows.length}
          </Badge>
        </div>

        {/* Desktop */}
        <div className="d-none d-lg-block">
          <Table hover size="sm" className="mb-0 small align-middle">
            <thead className="text-center">
              <tr>
                <th style={{ width: 28 }} />
                <th className="text-primary fw-semibold text-start">Date</th>
                <th className="text-primary fw-semibold text-start">Tariff</th>
                <th className="text-primary fw-semibold acc-list-num">Claimed</th>
                <th className="text-primary fw-semibold acc-list-num">Paid</th>
                <th className="text-primary fw-semibold acc-list-num">To Patient</th>
                <th className="text-primary fw-semibold text-center">Status</th>
                <th className="text-primary fw-semibold text-start">Reason / Match</th>
                {actionable && (
                  <th className="text-primary fw-semibold text-center" style={{ width: 170 }}>
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const g = row.group;
                const sub = g.subtotals || {};
                const m = g.matchedClaim || {};
                const meta = badgeFor(g);
                const isOpen = expanded.has(row.id);
                const canAllocate = g.status === "unallocated";
                return (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td className="text-center">
                        {row.lines.length > 0 && (
                          <Button
                            variant="link"
                            className="p-0 text-primary text-decoration-none"
                            aria-label={isOpen ? "Hide lines" : "Show lines"}
                            onClick={() => toggleExpanded(row.id)}
                          >
                            <i className={`fa-solid fa-chevron-${isOpen ? "down" : "right"}`} />
                          </Button>
                        )}
                      </td>
                      <td className="fw-semibold">{formatGroupDate(g.date)}</td>
                      <td>{tariffSummary(row.lines)}</td>
                      <td className="acc-list-num">{fmtZAR(sub.claimed || 0)}</td>
                      <td className="acc-list-num">{fmtZAR(sub.paid || 0)}</td>
                      <td className="acc-list-num">{fmtZAR(sub.movedToPatient || 0)}</td>
                      <td className="text-center">
                        <Badge bg={meta.bg} pill>
                          <i className={`fa-solid ${meta.icon} me-1`} />
                          {meta.label}
                        </Badge>
                      </td>
                      <td>
                        {m.transactionNumber && (
                          <div>
                            Claim #{m.transactionNumber}
                            {m.statusAfter ? (
                              <span className="text-muted"> • {m.statusAfter}</span>
                            ) : null}
                          </div>
                        )}
                        {g.reason && (
                          <div className="text-muted">{g.reason}</div>
                        )}
                        {!m.transactionNumber && !g.reason && (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      {actionable && (
                        <td className="text-center">
                          {canAllocate ? (
                            <div className="d-flex gap-1 justify-content-center">
                              <Button
                                size="sm"
                                variant="primary"
                                disabled={busy === row.id}
                                onClick={() => handleAllocate(patient, row)}
                              >
                                {busy === row.id ? (
                                  <Spinner size="sm" animation="border" />
                                ) : (
                                  "Manually Match"
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                disabled={busy === row.id}
                                onClick={() => handleDismiss(row)}
                              >
                                Dismiss
                              </Button>
                            </div>
                          ) : (
                            <span className="text-muted">Manual review</span>
                          )}
                        </td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={actionable ? 9 : 8} className="p-0">
                          {renderLineDetail(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>

        {/* Mobile */}
        <div className="d-lg-none">
          {rows.map((row) => {
            const g = row.group;
            const sub = g.subtotals || {};
            const m = g.matchedClaim || {};
            const meta = badgeFor(g);
            const isOpen = expanded.has(row.id);
            const canAllocate = g.status === "unallocated";
            return (
              <div className="acc-card" key={`m-${row.id}`}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{formatGroupDate(g.date)}</span>
                  <Badge bg={meta.bg} pill>
                    <i className={`fa-solid ${meta.icon} me-1`} />
                    {meta.label}
                  </Badge>
                </div>
                <div className="acc-card-body">
                  <div className="acc-card-field">
                    <span className="acc-card-label">Tariff</span>
                    <span className="acc-card-value">{tariffSummary(row.lines)}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Claimed</span>
                    <span className="acc-card-value acc-list-num">{fmtZAR(sub.claimed || 0)}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Paid</span>
                    <span className="acc-card-value acc-list-num">{fmtZAR(sub.paid || 0)}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">To Patient</span>
                    <span className="acc-card-value acc-list-num">{fmtZAR(sub.movedToPatient || 0)}</span>
                  </div>
                  {m.transactionNumber && (
                    <div className="acc-card-field">
                      <span className="acc-card-label">Match</span>
                      <span className="acc-card-value">
                        Claim #{m.transactionNumber}
                        {m.statusAfter ? ` • ${m.statusAfter}` : ""}
                      </span>
                    </div>
                  )}
                  {g.reason && (
                    <div className="acc-card-field">
                      <span className="acc-card-label">Reason</span>
                      <span className="acc-card-value">{g.reason}</span>
                    </div>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {row.lines.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => toggleExpanded(row.id)}
                    >
                      <i className={`fa-solid fa-chevron-${isOpen ? "down" : "right"} me-2`} />
                      {isOpen ? "Hide lines" : "Show lines"}
                    </Button>
                  )}
                  {actionable && canAllocate && (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={busy === row.id}
                        onClick={() => handleAllocate(patient, row)}
                      >
                        {busy === row.id ? <Spinner size="sm" animation="border" /> : "Manually Match"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={busy === row.id}
                        onClick={() => handleDismiss(row)}
                      >
                        Dismiss
                      </Button>
                    </>
                  )}
                </div>
                {isOpen && renderLineDetail(row)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (groups.length === 0) {
    return <p className="text-muted mb-0">No claims recorded on this ERA.</p>;
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="py-1 my-2 small">
          {error}
        </Alert>
      )}

      {groups.map((patient) => (
        <div key={patient.key} className="mb-3 p-3 bg-white border rounded shadow-sm">
          {/* Patient header — the person the scheme paid for */}
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
            <div>
              <h6 className="mb-1 text-primary fw-bold">{patient.patientName}</h6>
              <div className="small text-muted">
                Medical Aid #: <span className="fw-semibold">{patient.accountMedicalAidNumber || "—"}</span>
                {patient.dependantCode && (
                  <span className="fw-semibold"> ({patient.dependantCode})</span>
                )}
                {patient.accountReference && (
                  <>
                    {"  •  "}Account Ref:{" "}
                    <span className="fw-semibold">{patient.accountReference}</span>
                  </>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {patient.matched.length > 0 && (
                <Badge bg="success" pill>{patient.matched.length} matched</Badge>
              )}
              {patient.unmatched.length > 0 && (
                <Badge bg="danger" pill>{patient.unmatched.length} unmatched</Badge>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleGoToAccount(patient)}
              >
                <i className="fa-solid fa-user me-2" />
                Account
              </Button>
            </div>
          </div>

          {renderClaimsBlock({
            title: "Matched",
            tone: "success",
            rows: patient.matched,
            actionable: false,
            patient,
          })}
          {renderClaimsBlock({
            title: "Unmatched",
            tone: "danger",
            rows: patient.unmatched,
            actionable: true,
            patient,
          })}
        </div>
      ))}

      {receiptModalShow && (
        <CreateReceiptModal
          show={receiptModalShow}
          onHide={handleReceiptClosed}
          patient={allocCtx.patient}
          account={allocCtx.account}
        />
      )}
    </>
  );
}
